import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Chip, Skeleton, Tooltip, Divider, List, ListItem, ListItemText, Button } from '@mui/material';
import {
  Shield as ShieldIcon,
  LocalGasStation as FuelIcon,
  Inventory as CargoIcon,
  Favorite as HullIcon,
  Warning as WarningIcon,
  Anchor as DockedIcon,
  Sell as SellIcon,
} from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import type { Ship, ShipCargo } from '../../types/gameState';

interface ShipPanelProps {
  playerId: string;
}

interface StatusBarProps {
  label: string;
  current: number;
  max: number;
  color: 'error' | 'warning' | 'info' | 'success' | 'primary' | 'secondary';
  icon: React.ReactNode;
  showWarning?: boolean;
}

function StatusBar({ label, current, max, color, icon, showWarning }: StatusBarProps) {
  const percentage = max > 0 ? (current / max) * 100 : 0;
  const isLow = percentage < 25;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ color: isLow && showWarning ? 'error.main' : `${color}.main`, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {label}
          </Typography>
          {isLow && showWarning && (
            <Tooltip title="Low level warning">
              <WarningIcon sx={{ fontSize: 14, color: 'error.main', ml: 0.5 }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: isLow && showWarning ? 'error.main' : 'text.primary' }}>
          {current.toLocaleString()}/{max.toLocaleString()}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={isLow && showWarning ? 'error' : color}
        sx={{ height: 8, borderRadius: 1, backgroundColor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 1 } }}
      />
    </Box>
  );
}

export function ShipPanel({ playerId }: ShipPanelProps) {
  const [ship, setShip] = useState<Ship | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sendMessage } = useChatContext();
  const { setSelectedPanel } = useConsole();

  useEffect(() => {
    async function fetchShip() {
      setIsLoading(true);
      setError(null);

      const logId = logService.api('GET', `/api/players/${playerId}/ships`, {}, 'pending');
      const startTime = Date.now();

      try {
        const response = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}/ships`);
        const duration = Date.now() - startTime;

        if (!response.ok) {
          logService.updateLog(logId, { status: 'error', duration, message: `HTTP ${response.status}` });
          setError('Failed to load ship data');
          setIsLoading(false);
          return;
        }

        const result = await response.json();
        if (!result.success || !result.data) {
          logService.updateLog(logId, { status: 'error', duration, message: 'No ship data' });
          setError('No ship data available');
          setIsLoading(false);
          return;
        }

        // Get first non-destroyed ship
        const ships = result.data;
        const activeShip = ships.find((s: Ship) => !s.is_destroyed) || ships[0];

        if (!activeShip) {
          logService.updateLog(logId, { status: 'error', duration, message: 'No active ship' });
          setError('No ship available');
          setIsLoading(false);
          return;
        }

        // Now get full ship details with cargo, etc.
        const shipLogId = logService.api('GET', `/api/ships/${activeShip.id}`, {}, 'pending');
        const shipStart = Date.now();
        const shipRes = await fetch(`${DATABASE_SERVICE_URL()}/api/ships/${activeShip.id}`);
        const shipDuration = Date.now() - shipStart;

        if (!shipRes.ok) {
          logService.updateLog(shipLogId, { status: 'error', duration: shipDuration, message: `HTTP ${shipRes.status}` });
          setError('Failed to load ship details');
          setIsLoading(false);
          return;
        }

        const shipResult = await shipRes.json();
        if (!shipResult.success || !shipResult.data) {
          logService.updateLog(shipLogId, { status: 'error', duration: shipDuration, message: 'No ship details' });
          setError('No ship details available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(logId, { status: 'success', duration });
        logService.updateLog(shipLogId, { status: 'success', duration: shipDuration });
        setShip(shipResult.data);
      } catch (err) {
        const duration = Date.now() - startTime;
        logService.updateLog(logId, { status: 'error', duration, message: err instanceof Error ? err.message : 'Unknown error' });
        setError('Failed to load ship data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchShip();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height={8} sx={{ my: 1 }} />
              <Skeleton variant="rectangular" height={8} sx={{ my: 1 }} />
              <Skeleton variant="rectangular" height={8} sx={{ my: 1 }} />
              <Skeleton variant="rectangular" height={8} sx={{ my: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error || !ship) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error || 'No ship data available'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const cargo = ship.cargo || [];

  const handleSellAll = () => {
    if (cargo.length === 0) return;

    setSelectedPanel('chat');
    sendMessage('Please sell all my cargo');
  };

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
            Ship & Cargo
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {ship.name || 'Unnamed Ship'}
              </Typography>
              {ship.ship_class && (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {ship.ship_class.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {!!ship.is_docked && (
                <Chip icon={<DockedIcon sx={{ fontSize: 14 }} />} label="Docked" size="small" color="info" variant="outlined" sx={{ height: 24 }} />
              )}
              {!!ship.is_destroyed && <Chip label="Destroyed" size="small" color="error" sx={{ height: 24 }} />}
            </Box>
          </Box>

          <StatusBar label="Hull" current={ship.hull_current} max={ship.hull_max} color="error" icon={<HullIcon sx={{ fontSize: 16 }} />} showWarning />
          <StatusBar label="Shields" current={ship.shields_current} max={ship.shields_max} color="info" icon={<ShieldIcon sx={{ fontSize: 16 }} />} showWarning />
          <StatusBar label="Fuel" current={ship.fuel_current} max={ship.fuel_max} color="warning" icon={<FuelIcon sx={{ fontSize: 16 }} />} showWarning />
          <StatusBar label="Cargo" current={ship.cargo_used} max={ship.cargo_max} color="secondary" icon={<CargoIcon sx={{ fontSize: 16 }} />} />

          {!ship.insurance_valid && (
            <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, backgroundColor: 'error.dark', color: 'error.contrastText', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Insurance Expired
              </Typography>
            </Box>
          )}

          {/* Cargo Hold Section */}
          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem' }}>
              Cargo Hold
            </Typography>
            {cargo.length > 0 && ship.is_docked && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<SellIcon />}
                onClick={handleSellAll}
                sx={{ height: 24, fontSize: '0.7rem' }}
              >
                Sell All
              </Button>
            )}
          </Box>

          {/* Cargo List */}
          {cargo.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              Cargo hold is empty
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {cargo.map((item: ShipCargo, index: number) => (
                <Box key={item.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ px: 1, py: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.commodity_name || item.commodity?.name || 'Unknown Commodity'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}>
                            {item.quantity} tons
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {item.category && <Chip label={item.category} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          {!!item.is_stolen && <Chip label="Stolen" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          {!!item.is_damaged && <Chip label="Damaged" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          {item.purchased_price !== null && (
                            <Chip label={`¤${item.purchased_price.toLocaleString()}/ton`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
